import { Device } from '@goodboydigital/astro';
import { Sprite, Text } from 'pixi.js';
import { Signal } from 'typed-signals';

import { Shared } from '../App';
import { NineSliceButton } from './buttons/NineSliceButton';
import { SwipeScroller } from './SwipeScroller';

const defaultOpts = {
    h: 180,
    value: 'A',
};

export type ShopPanelOpts = typeof defaultOpts;

const LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

/*
* Shop panel is intended to provide the base functionality and buttons for the panel class
* In use, it will require a child to be added, the child will be positioned in the center of the card
*
* To make use of the buttons, connect to the signals exposed from the panel
*/
export class LetterSelector extends SwipeScroller
{
    public signals: {
        onArrowDown: Signal<() => void>,
        onArrowUp: Signal<() => void>,
    };

    protected downArrowBtn: NineSliceButton;
    protected upArrowBtn: NineSliceButton;

    constructor(opts: Partial<ShopPanelOpts> = {})
    {
        const o = { ...defaultOpts, ...opts };
        const items = LETTERS.split('').map((letter) =>
        {
            const text = new Text(letter, {
                fontFamily: 'lubalin',
                fill: 0xFFFFFF,
                stroke: 0x333333,
                strokeThickness: 12,
                lineJoin: 'round',
                align: 'center',
                fontSize: 80,
            });

            text.anchor.set(0.5);

            return text;
        });

        super({
            index: LETTERS.indexOf(o.value),
            length: LETTERS.length,
            offsetY: 0,
            width: 160,
            height: 200,
            spacing: 100,
        }, (index: number) => items[index]);

        this.signals = {
            onArrowDown: new Signal<() => void>(),
            onArrowUp: new Signal<() => void>(),
        };

        const downArrowBtn = Sprite.from(Shared.assets.getTexture('updown-arrow'));

        this.downArrowBtn = new NineSliceButton({
            icon: downArrowBtn,
            w: 120,
            h: 80,
        });
        this.downArrowBtn.view.y += (o.h / 2) + 10;
        this.downArrowBtn.onPress.connect(() =>
        {
            this.signals.onArrowDown.emit();
            this.selectNext();
        });
        this.view.addChild(this.downArrowBtn.view);

        const upArrowBtn = Sprite.from(Shared.assets.getTexture('updown-arrow'));

        upArrowBtn.rotation = Math.PI;
        this.upArrowBtn = new NineSliceButton({
            icon: upArrowBtn,
            w: 120,
            h: 80,
        });
        this.upArrowBtn.onPress.connect(() =>
        {
            this.signals.onArrowUp.emit();
            this.selectPrev();
        });

        this.upArrowBtn.view.y = -(o.h / 2);
        this.view.addChild(this.upArrowBtn.view);

        this.upArrowBtn.view.visible = Device.desktop;
        this.downArrowBtn.view.visible = Device.desktop;
    }

    public selectNext(): void
    {
        this.nextItem();
    }

    public selectPrev(): void
    {
        this.prevItem();
    }

    public get text(): string { return LETTERS[this.getCurrentIndex()]; }
    public set text(val: string)
    {
        this.setCurrentIndex(LETTERS.indexOf(val) || 0);
    }
}
